// Configuration constants - easily adjustable
export const CONFIG = {
  // Vault ID settings
  VAULT_ID_LENGTH: 15,
  VAULT_ID_ALPHABET: '0123456789ABCDEFGHJKLMNOPQRSTUVWXYZ', // No 'I' to avoid confusion with 'l' and '1'

  // Size limits
  MAX_VAULT_SIZE_BYTES: 5 * 1024 * 1024, // 5MB
  LOCAL_CACHE_MAX_BYTES: 50 * 1024 * 1024, // 50MB total cache

  // scrypt parameters (tuned for ~32MB memory usage)
  // N=2^14=16384 with r=8 uses 16384 * 128 * 8 = 16MB per block
  // This provides good security with reasonable performance
  SCRYPT_N: 16384, // CPU/memory cost (power of 2)
  SCRYPT_R: 8, // Block size
  SCRYPT_P: 1, // Parallelization
  SCRYPT_DKLEN: 32, // Derived key length (256 bits)

  // Salt sizes
  SALT_LENGTH: 16, // 128 bits

  // AES-GCM settings
  AES_KEY_LENGTH: 256,
  AES_IV_LENGTH: 12, // 96 bits as recommended for GCM

  // HKDF contexts
  HKDF_VERIFY_CONTEXT: 'vault-verify-v1',
  HKDF_CACHE_CONTEXT: 'vault-cache-v1',

  // WebAuthn settings
  WEBAUTHN_RP_NAME: 'Vault',
  WEBAUTHN_RP_ID: typeof window !== 'undefined' ? window.location.hostname : '',

  // Cache settings
  CACHE_DB_NAME: 'vault-cache',
  CACHE_DB_VERSION: 1,

  // API endpoints
  API_BASE: '/api',
} as const

export type AuthType = 'passphrase' | 'passkey'

export interface VaultMeta {
  authType: AuthType
  salt: string // base64
  verificationTag: string // base64
  createdAt: string
  updatedAt: string
  size: number
  // For passkey vaults
  credentialId?: string // base64
  publicKey?: string // base64
}

export interface Folder {
  id: string
  name: string
  parentId: string | null
  createdAt: string
}

export interface Tag {
  id: string
  name: string
  color: string // hex
}

export interface VaultItem {
  id: string
  type: 'bookmark' | 'note'
  title: string
  content: string // URL for bookmark, markdown for note
  description?: string
  folderId: string | null
  tagIds: string[]
  createdAt: string
  updatedAt: string
}

export interface VaultData {
  version: 1
  folders: Folder[]
  tags: Tag[]
  items: VaultItem[]
}

export const createEmptyVaultData = (): VaultData => ({
  version: 1,
  folders: [],
  tags: [],
  items: [],
})
